import TableContainer from "Components/Common/TableContainer";
import { Card, CardBody, Col } from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import React, { useCallback, useEffect, useMemo, useState } from "react";
// import Loader from "Components/Common/Loader";
import { useNavigate } from "react-router-dom";

import DeleteModal from "Components/Common/DeleteModal";
import ManageClientModal from "./ManageClientModal";
import ClientListFilter from "./ClientListFilter";

import {
  addNewClient,
  deleteClient,
  getClients,
  updateClient,
} from "slices/thunks";

import {
  AddClientFormParams,
  ClientsPayloadData,
  EditClientFormParams,
} from "types/client.api";
import { selectClientsState } from "slices/clients/reducer";
import { getAvatarStyle, getInitials } from "utils/getAvatarColor";
import ActionMenu from "Components/Common/ActionMenu";
import { ErrorHandler } from "utils/toast";

interface ClientListProps {
  showManageClientModal: boolean;
  setShowManageClientModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const ClientList: React.FC<ClientListProps> = ({
  showManageClientModal,
  setShowManageClientModal,
}) => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();

  const [selectedClient, setSelectedClient] =
    useState<ClientsPayloadData | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilter, setShowFilter] = useState(false);

  const [deleteModal, setDeleteModal] = useState(false);

  const [filters, setFilters] = useState<{ sortBy: string | null }>({
    sortBy: null,
  });

  const clientSelector = createSelector(selectClientsState, (state) => ({
    clientItems: state.clients,
    isLoading: state.status.fetch,
    meta: state.pagination,
  }));

  const { clientItems, isLoading, meta } = useSelector(clientSelector);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const toggle = useCallback(() => {
    if (showManageClientModal) {
      setShowManageClientModal(false);
      setSelectedClient(null);
    } else {
      setShowManageClientModal(true);
    }
  }, [showManageClientModal, setShowManageClientModal]);

  const handleSelectedClientClick = useCallback(
    (client: ClientsPayloadData) => {
      setSelectedClient(client);
      toggle();
    },
    [toggle],
  );

  const onClickDelete = (client: ClientsPayloadData) => {
    setSelectedClient(client);
    setDeleteModal(true);
  };

  const handleDeleteSelectedClient = () => {
    if (selectedClient) {
      dispatch(deleteClient(selectedClient.id));
      setDeleteModal(false);
      setSelectedClient(null);
    }
  };

  const handleFilterChange = (data: { sortBy: string | null }) => {
    setFilters(data);
    console.log("Filters applied:", data);

    setShowFilter(false);
  };

  useEffect(() => {
    const params = searchTerm
      ? { search: searchTerm, sort_by: filters.sortBy }
      : { page: currentPage, sort_by: filters.sortBy };
    dispatch(getClients(params));
  }, [currentPage, dispatch, filters.sortBy, searchTerm]);

  const columns = useMemo(
    () => [
      {
        header: "CLIENT",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => {
          let value = cell.getValue();
          return (
            <div className="d-flex align-items-center gap-2">
              <div className="avatar-circle" style={getAvatarStyle(value)}>
                {getInitials(value)}
              </div>

              <div className="fw-normal text-black2D fs-14 font-primary">
                {value}
              </div>
            </div>
          );
        },
      },
      {
        header: "COMPANY",
        accessorKey: "company_name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()?.trim() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "PHONE",
        accessorKey: "phone",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "EMAIL",
        accessorKey: "email",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },

      {
        header: "ACTION",
        enableSorting: false,
        cell: (cellProps: any) => {
          const rowClient = cellProps.row.original;

          return (
            <ActionMenu
              onView={() =>
                navigate("/client-details", {
                  state: { id: rowClient.id },
                })
              }
              isView={true}
              onEdit={() => handleSelectedClientClick(rowClient)}
              onDelete={() => onClickDelete(rowClient)}
              isDelete={true}
              isEdit
            />
          );
        },
      },
    ],
    [handleSelectedClientClick, navigate],
  );

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteSelectedClient}
        onCloseClick={() => {
          setDeleteModal(false);
          setSelectedClient(null);
        }}
      />

      <ManageClientModal
        isOpen={showManageClientModal}
        toggleModal={toggle}
        isEdit={!!selectedClient}
        initialData={selectedClient}
        onSubmit={async (payload) => {
          try {
            if (selectedClient) {
              await dispatch(
                updateClient(payload as EditClientFormParams),
              )?.unwrap();
            } else {
              await dispatch(
                addNewClient(payload as AddClientFormParams),
              )?.unwrap();
            }
          } catch (error) {
            // ErrorHandler(error);
          }
        }}
      />

      <ClientListFilter
        show={showFilter}
        onCloseClick={() => setShowFilter(false)}
        onFilterChange={handleFilterChange}
      />

      <Col xxl={12}>
        <Card className="br-10 bg-white">
          <CardBody>
            <TableContainer
              columns={columns}
              data={clientItems ?? []}
              isGlobalFilter={true}
              customPageSize={meta?.per_page}
              divClass="table-responsive table-card mb-2 bg-white"
              tableClass="align-middle table-nowrap table-borderless"
              thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5 category-table"
              theadClass="table-light"
              SearchPlaceholder="Search clients"
              onSearch={handleSearch}
              onPageChange={handlePageChange}
              isClientDetails={false}
              currentPage={meta?.current_page}
              totalPages={meta?.last_page}
              toPage={meta?.to}
              fromPage={meta?.from}
              totalItems={meta?.total}
              isListFilter={true}
              onListFilter={() => setShowFilter(true)}
              isInitialLoading={isLoading === "loading" && !clientItems?.length}
              isPaginationLoading={
                isLoading === "loading" && clientItems?.length > 0
              }
            />

            {/* <ToastContainer closeButton={false} limit={1} /> */}
          </CardBody>
        </Card>
      </Col>
    </>
  );
};

export default React.memo(ClientList);
